import { AppBar, Toolbar, Box, Typography, Button, useMediaQuery, Drawer, Stack, IconButton, ListItemText, Divider, ListItem, Icon, ListItemButton, FormControl, MenuItem, Select, Badge, CircularProgress, Tooltip, useTheme } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon, Security, InfoOutlined as InfoOutlinedIcon, NotificationsActiveOutlined, DarkModeOutlined, LightModeOutlined } from "@mui/icons-material";
import { Link as RouterLink, useLocation, useNavigate, Link } from 'react-router-dom'
import logoUrl from '@assets/Logo_Assets-03.svg'
import { useSelector } from 'react-redux'
import userSelector from '@/store/selectors/userSelector'
import { ROUTES } from '@/routes'
import { useEffect, useRef, useState } from 'react'
import { primaryColor, useColorMode } from '@/theme'
import ListItemButtonSelected from './ListItemButtonSelected';
import { SidebarAdminButtons } from './SidebarAdmin';
import { SidebarClientButtons } from './Sidebar';
import queryClient from '@/helpers/queryClient';
import { DrawerContext } from './DrawerContext';
import useMunicipalityImageUrl from '@/hooks/useMunicipalityImageUrl';
import DashSeparator from '@/components/DashSeparator';
import { setActiveMunicipalityId } from '@/helpers/activeMunicipality';
import { useDispatch } from 'react-redux';
import { updateUser } from '@/store/reducers/userSlice';
import { useGetMunicipalities } from '@/helpers/getMunicipality';
import { setActiveMunicipality } from '@/store/reducers/municipalitySlice';
import { activeMunicipalityIdSelector } from '@/store/selectors/municipalitySelector';
import type { AppDispatch } from '@/store';
import { useGetSubscription } from '@/helpers/subscriptionInfo';
import { convertDate } from '@/helpers/misc';
import { useDismissInspectionAlert, useGetInspectionAlerts, type InspectionAlert } from '@/helpers/inspectionAlerts';
import { useGetConsumptionReminderAlerts } from '@/helpers/consumptionReminderAlerts';
import InspectionAlertCard from '@/components/alerts/InspectionAlertCard';
import ConsumptionReminderAlertCard from '@/components/alerts/ConsumptionReminderAlertCard';

const DrawerMenuId = "primary-search-account-drawer-menu";

const TopMenu = () => {
  const theme = useTheme();
  const { mode, toggleColorMode } = useColorMode();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(userSelector);
  const navigate = useNavigate();
  const location = useLocation();

  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isAdmin = user?.type === 'admin';
  const isClient = user?.type === 'client';
  const logoDestination = user ? (isAdmin ? ROUTES.ADMIN_DASHBOARD : ROUTES.DASHBOARD) : ROUTES.HOME;
  const subscription = useGetSubscription();

  const isHomepage = location.pathname === ROUTES.HOME;
  const isClientPage =
    location.pathname.startsWith("/client") || location.pathname.startsWith("/onboarding");
  const showTrialNotice =
    isClient &&
    isClientPage &&
    subscription.plan === "TRIAL" &&
    !!subscription.endsAt;
  const showStandardExpiringNotice =
    isClient &&
    isClientPage &&
    subscription.plan === "STANDARD" &&
    !!subscription.endsAt &&
    subscription.daysRemaining !== null &&
    subscription.daysRemaining < 14;

  const showNotice =
    location.pathname !== ROUTES.CLIENT_SUBSCRIPTION &&
    (showTrialNotice || showStandardExpiringNotice);
  const noticeEndsAtLabel = showNotice ? convertDate(subscription.endsAt ?? undefined) : "";
  const noticeText = subscription.plan === "STANDARD" 
    ? `Váš tarif končí ${noticeEndsAtLabel}.` 
    : `Vaše zkušební období končí ${noticeEndsAtLabel}.`;
  const bottomBorder = location.pathname !== "/";

  const anchorElementRef = useRef<null | HTMLElement>(null!);
  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);
  const [isDrawerMenuOpen, setIsDrawerMenuOpen] = useState(false);
  const [isAlertsDrawerOpen, setIsAlertsDrawerOpen] = useState(false);
  const municipalityImageUrl = useMunicipalityImageUrl();
  const { data: municipalities = [] } = useGetMunicipalities({ enabled: !!user && isClient });
  const activeMunicipalityId = useSelector(activeMunicipalityIdSelector);
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<string>('');
  const { data: inspectionAlerts = [], isLoading: isInspectionAlertsLoading } = useGetInspectionAlerts({
    enabled: isClient && isClientPage,
  });
  const { data: consumptionReminderAlerts = [], isLoading: isConsumptionReminderAlertsLoading } = useGetConsumptionReminderAlerts({
    enabled: isClient && isClientPage,
  });
  const dismissInspectionAlert = useDismissInspectionAlert();
  const alertsCount = inspectionAlerts.length + consumptionReminderAlerts.length;
  const isAnyAlertsLoading = isInspectionAlertsLoading || isConsumptionReminderAlertsLoading;

  useEffect(() => {
    setSelectedMunicipalityId(activeMunicipalityId ?? municipalities[0]?.id ?? '');
  }, [activeMunicipalityId, municipalities]);

  const handleLogout = async () => {
    await queryClient.invalidateQueries();
    navigate(ROUTES.LOGOUT);
  };

  const handleMunicipalityChange = async (event: SelectChangeEvent<string>) => {
    const municipalityId = event.target.value || '';
    setSelectedMunicipalityId(municipalityId);
    dispatch(setActiveMunicipality(municipalityId || null));
    setActiveMunicipalityId(municipalityId || null);
    await queryClient.invalidateQueries();
    await dispatch(updateUser());
  };

  const handleDrawerMenuOpen = () => {
    setIsDrawerMenuOpen(true);
  };

  const handleDrawerMenuClose = () => {
    setIsDrawerMenuOpen(false);
  };

  const handleAlertsDrawerOpen = () => {
    setIsAlertsDrawerOpen(true);
  };

  const handleAlertsDrawerClose = () => {
    setIsAlertsDrawerOpen(false);
  };

  const handleDismissAlert = async (alert: InspectionAlert) => {
    try {
      await dismissInspectionAlert.mutateAsync({
        inspectionId: alert.inspectionId,
        dueDate: alert.dueDate,
      });
    } catch {
      // Keep the card visible when dismissing fails.
    }
  };

  const renderDrawerMenu = (
    <Drawer
      anchor="right"
      open={isDrawerMenuOpen}
      keepMounted={true}
      onClose={handleDrawerMenuClose}
      id={DrawerMenuId}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.primary.main,
          padding: "24px 24px 0 24px",
          width: "100%",
          zIndex: 99,
        },
      }}
    >
      <DrawerContext.Provider value={true}>
        <Box sx={{ color: "white" }}>
          <Stack justifyContent="space-between" direction="row" mb={5} alignItems="center">
            <Box
              component={RouterLink}
              to={logoDestination}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1 },
                flex: 1,
                minWidth: 0,
                textDecoration: 'none',
                color: 'white'
              }}
              onClick={handleDrawerMenuClose}
            >
              <Box
                component="img"
                src={logoUrl}
                alt="Energovizor logo"
                sx={{
                  height: { xs: 40, sm: 50 },
                  flexShrink: 0
                }}
              />
              <Typography
                variant='h5'
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: 'white'
                }}
              >
                Energovizor
              </Typography>
            </Box>
            <IconButton
              edge="end"
              aria-label="close drawer"
              onClick={handleDrawerMenuClose}
              sx={{ flexShrink: 0, color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack
            gap={2}
            sx={{
              "& > * > .MuiListItemButton-root, & > .MuiListItemButton-root": {
                "&:hover": {
                  backgroundColor: (theme) =>
                    `${theme.palette.primary.light} !important`,
                  color: `white !important`,
                },
              },
              "& > * > .Mui-selected, & > .Mui-selected": {
                p: 1,
                px: 2.5,
                backgroundColor: (theme) =>
                  `${theme.palette.primary.dark} !important`,
                color: `white !important`,
                "& > * > .MuiTypography-root": {
                  fontWeight: "600",
                },
              },
            }}
          >
            <ListItemButtonSelected urlLevel={1} component={Link} to={ROUTES.HOME} isCollapsed={true}>
              <ListItemText>Úvodní stránka</ListItemText>
            </ListItemButtonSelected>
            <Divider sx={{ bgcolor: 'common.white' }} />
            {user ? (
              <Stack gap={1}>
                <ListItem
                  sx={{
                    justifyContent: "space-between",
                    [theme.breakpoints.down("sm")]: {
                      flexDirection: "column",
                      justifyContent: "normal",
                      alignItems: "normal",
                    },
                  }}
                >
                  <Stack
                    direction={"row"}
                    gap={1}
                    alignItems={"center"}
                    justifyContent={"center"}
                    sx={{
                      [theme.breakpoints.down("sm")]: {
                        justifyContent: "normal",
                      },
                    }}
                  >
                    {user.type === "admin" && (
                      <Icon sx={{ color: 'white' }}>
                        <Security fontWeight={"600"} />
                      </Icon>
                    )}
                    <Typography sx={{ fontWeight: 600, color: 'white' }}>
                      {user.name}
                    </Typography>
                  </Stack>
                </ListItem>
                {isClient && municipalities.length > 1 && (
                  <ListItem sx={{ px: 0 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={selectedMunicipalityId || municipalities[0]?.id || ''}
                        onChange={handleMunicipalityChange}
                        sx={{
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.35)',
                          borderRadius: 1,
                          '& .MuiSelect-icon': { color: 'white' },
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }}
                      >
                        {municipalities.map((municipality) => (
                          <MenuItem key={municipality.id} value={municipality.id}>
                            {municipality.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </ListItem>
                )}
                {user?.type === "admin" ? (
                  <SidebarAdminButtons />
                ) : (
                  <SidebarClientButtons />
                )}
                <ListItemButton onClick={handleLogout}>
                  <ListItemText>Odhlásit</ListItemText>
                </ListItemButton>
              </Stack>
            ) : (
              <>
                <ListItemButtonSelected urlLevel={1} component={Link} to={ROUTES.LOGIN} sx={{ justifyContent: "center" }} isCollapsed={true}>
                  <ListItemText>Přihlášení</ListItemText>
                </ListItemButtonSelected>
                <ListItemButtonSelected urlLevel={1} component={Link} to={ROUTES.REGISTER} sx={{ justifyContent: "center" }} isCollapsed={true}>
                  <ListItemText>Registrace</ListItemText>
                </ListItemButtonSelected>
              </>
            )}
            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.45)' }} />
            <ListItemButton onClick={toggleColorMode} aria-label={mode === 'dark' ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'}>
              <Icon sx={{ color: 'white', mr: 1 }}>
                {mode === 'dark' ? <LightModeOutlined /> : <DarkModeOutlined />}
              </Icon>
              <ListItemText>{mode === 'dark' ? 'Světlý režim' : 'Tmavý režim'}</ListItemText>
            </ListItemButton>
          </Stack>
        </Box>
      </DrawerContext.Provider>
    </Drawer>
  );

  const renderAlertsDrawer = (
    <Drawer
      anchor="right"
      open={isAlertsDrawerOpen}
      onClose={handleAlertsDrawerClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 460 },
          p: 2,
        },
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Upozornění
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aktivních: {alertsCount}
            </Typography>
          </Box>
          <IconButton onClick={handleAlertsDrawerClose} aria-label="Zavřít upozornění">
            <CloseIcon />
          </IconButton>
        </Stack>

        {isAnyAlertsLoading ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : alertsCount === 0 ? (
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Momentálně nemáte žádná aktivní upozornění.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {consumptionReminderAlerts.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, px: 0.5 }}>
                Odběry spotřeby
              </Typography>
            )}
            {consumptionReminderAlerts.map((alert) => (
              <ConsumptionReminderAlertCard
                key={`${alert.collectionPointId}-${alert.dueDate}-${alert.alertType}`}
                alert={alert}
                showBuildingName
              />
            ))}

            {inspectionAlerts.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, px: 0.5, mt: 0.5 }}>
                Revize
              </Typography>
            )}
            {inspectionAlerts.map((alert) => (
              <InspectionAlertCard
                key={`${alert.inspectionId}-${alert.dueDate}-${alert.alertType}`}
                alert={alert}
                showBuildingName
                onDismiss={handleDismissAlert}
                dismissDisabled={dismissInspectionAlert.isPending}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );

  useEffect(() => {
    if (isTablet && isDropdownMenuOpen) setIsDropdownMenuOpen(false);

    if (!isTablet && isDrawerMenuOpen) setIsDrawerMenuOpen(false);
  }, [isTablet, isDropdownMenuOpen, isDrawerMenuOpen]);

  useEffect(() => {
    setIsDrawerMenuOpen(false);
    setIsAlertsDrawerOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const isLoggedIn = !!user;
    if (!isLoggedIn && isDropdownMenuOpen) setIsDropdownMenuOpen(false);
  }, [user, isDropdownMenuOpen]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position={isHomepage ? "absolute" : "fixed"}
        ref={anchorElementRef}
        id={"header"}
        sx={{
          backgroundColor: isHomepage ? "transparent" : "background.paper",
          boxShadow: "none",
          ...(bottomBorder && {
            boxShadow: `0 4px 3px -3px ${primaryColor}`,
          }),
        }}>
        <Toolbar sx={{ minHeight: 72, py: 1, px: { xs: 2, sm: 3 } }}>
          {/* Brand: Logo + Title */}
          <Box
            component={RouterLink}
            to={logoDestination}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1, md: 1.5 },
              textDecoration: 'none',
              color: 'inherit',
              minWidth: 0,
              flex: { xs: '1 1 auto', md: '0 0 auto' },
            }}
          >
            <Box
              component="img"
              src={logoUrl}
              alt="Energovizor logo"
              sx={{
                height: { xs: 40, sm: 50, md: 60 },
                flexShrink: 0
              }}
            />
            <Typography
              variant='h4'
              color='primary'
              sx={{
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              Energovizor
            </Typography>
            {municipalityImageUrl && (<Stack direction={"row"} alignItems={"center"}>
              <DashSeparator ml={2} mr={3}/>
              <Box
                component="img"
                src={municipalityImageUrl}
                alt={"Obec"}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  objectFit: "cover",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              />
            </Stack>)}
          </Box>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Right side: User email and logout button */}
          {user ? (
            <Box sx={{ alignItems: 'center', gap: 2, display: { xs: "none", sm: "none", md: "flex" } }}>
              {isClient && isClientPage && (
                <IconButton onClick={handleAlertsDrawerOpen} aria-label="Upozornění na revize">
                  <Badge badgeContent={alertsCount} color="error" max={99}>
                    <NotificationsActiveOutlined />
                  </Badge>
                </IconButton>
              )}
              {isClient && municipalities.length > 1 && (
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <Select
                    value={selectedMunicipalityId || municipalities[0]?.id || ''}
                    onChange={handleMunicipalityChange}
                    sx={{
                      bgcolor: isHomepage ? 'rgba(255,255,255,0.12)' : 'background.paper',
                      color: isHomepage ? 'white' : 'text.primary',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isHomepage ? 'rgba(255,255,255,0.35)' : 'divider',
                      },
                      '& .MuiSelect-icon': {
                        color: isHomepage ? 'white' : 'text.secondary',
                      },
                    }}
                  >
                    {municipalities.map((municipality) => (
                      <MenuItem key={municipality.id} value={municipality.id}>
                        {municipality.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Typography variant="body2" sx={{ color: isHomepage ? 'white' : 'text.secondary' }}>
                {user.name}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleLogout}
                sx={{ color: isHomepage ? 'white' : 'primary.main' }}
              >
                Odhlásit
              </Button>
            </Box>
          ) : (
            <Box sx={{ alignItems: 'center', gap: 2, display: { xs: "none", sm: "none", md: "flex" } }}>
              <Button
                component={RouterLink}
                to={ROUTES.LOGIN}
                variant="text"
                sx={{ color: isHomepage ? 'white' : 'primary.main', fontWeight: 600 }}
              >
                Přihlášení
              </Button>
              <Button
                component={RouterLink}
                to={ROUTES.REGISTER}
                variant="contained"
                color="primary"
                sx={{
                  borderRadius: '20px',
                  px: 3,
                  bgcolor: '#8FA93A',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#7d9432'
                  }
                }}
              >
                Registrovat
              </Button>
            </Box>
          )}
          <Tooltip title={mode === 'dark' ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'}>
            <IconButton
              onClick={toggleColorMode}
              aria-label={mode === 'dark' ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'}
              sx={{
                ml: { xs: 0, md: 1 },
                color: isHomepage ? 'white' : 'text.primary',
              }}
            >
              {mode === 'dark' ? <LightModeOutlined /> : <DarkModeOutlined />}
            </IconButton>
          </Tooltip>
          <Box sx={{ display: { xs: "flex", sm: "flex", md: "none" } }}>
            {isClient && isClientPage && (
              <IconButton
                edge="start"
                sx={{ color: isHomepage ? "white" : "text.primary" }}
                aria-label="open alerts"
                onClick={handleAlertsDrawerOpen}
              >
                <Badge badgeContent={alertsCount} color="error" max={99}>
                  <NotificationsActiveOutlined />
                </Badge>
              </IconButton>
            )}
            <IconButton
              edge="start"
              sx={{ color: isHomepage ? "white" : "text.primary" }}
              aria-label="open drawer"
              onClick={handleDrawerMenuOpen}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
        {showNotice && (
          <Box
            sx={{
              width: "100%",
              background: 'linear-gradient(90deg, #F9FA41 0%, #8FA93A 100%)',
              py: 1.5,
              px: { xs: 2, md: 4 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              boxShadow: "inset 0px -1px 0px rgba(0,0,0,0.1)",
            }}
          >
            <InfoOutlinedIcon sx={{ color: "#1A2E05", fontSize: 24, display: { xs: "none", sm: "block" } }} />
            <Typography
              variant="body2"
              sx={{
                color: "#1A2E05",
                textAlign: "center",
                fontWeight: 600,
                fontSize: "0.9rem"
              }}
            >
              {noticeText}
            </Typography>
            <Button
              component={RouterLink}
              to={ROUTES.CLIENT_SUBSCRIPTION}
              size="small"
              sx={{
                display: { xs: "none", sm: "block" },
                ml: 2,
                backgroundColor: "#1A2E05",
                color: "#FFF",
                borderRadius: "20px",
                px: 3,
                py: 0.5,
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { backgroundColor: "#2C4A0A" }
              }}
            >
              {subscription.plan === 'STANDARD' ? 'Prodloužit předplatné' : 'Zakoupit předplatné'}
            </Button>
          </Box>
        )}
      </AppBar>
      {renderDrawerMenu}
      {renderAlertsDrawer}
    </Box>
  )
}

export default TopMenu
